import axios from 'axios';
import { readStoredSub } from '../helpers/oidcStorage';

/**
 * Klient mot lyfti-api `/rest/applicationtemplate`. Brukes både av
 * `ConfigProvider` (for å bygge listen på Dashboard) og
 * `NewApplicationProvider` (for å rendre selve søknadsskjemaet med felt
 * lagret i form-builder, inkludert `restrictedVisibility`).
 *
 * `VITE_REST_URL` overstyrer base. Hvis ikke satt, derives base fra
 * `VITE_API_URL` ved å bytte stien til `/rest`.
 */

export type ServerTemplate = {
    id: number;
    title: string;
    type: number;
    alias: string;
    description: string;
    application: Record<string, any> | null;
    entity: number;
    // Liste over AKTIVERTE rapporttyper (opt-in). has_reports avledes herfra.
    report_config?: string[] | null;
    // Fra portal_application_setting: synlighet for søkere og rekkefølge.
    // `published` er ENESTE kilde til om skjemaet tilbys — se
    // portalApplicationTemplate::typeEntry.
    published?: boolean;
    sort?: number;
    // Effektiv orgnr-status, beregnet server-side av
    // portalApplicationTemplate::requiresOrgNr (eksplisitt flagg fra
    // form-builderen, ellers utledet fra skjemaet).
    requires_org_nr?: boolean;
    periods: {
        periods?: Array<{ id?: number; startdate: string; deadline: string }>;
        periods_text_inside?: string;
        periods_text_outside?: string;
        search_outside_periods?: boolean;
    } | null;
};

export const REST_BASE: string = (() => {
    const explicit = import.meta.env.VITE_REST_URL as string | undefined;
    if (explicit) return explicit.replace(/\/$/, '');
    const legacy = import.meta.env.VITE_API_URL as string | undefined;
    if (legacy) {
        try {
            const u = new URL(legacy);
            return `${u.protocol}//${u.host}/rest`;
        } catch {
            /* ignore */
        }
    }
    return '';
})();

/**
 * Henter alle templates en søker har tilgang til (resolvet via hostname →
 * entity i `host_entity`-setting).
 *
 * Returnerer `null` ved noen-feil — kallere må da falle tilbake på legacy-
 * mekanismen. Returnerer tom array hvis endpoint svarer men ikke har noen
 * maler for entiteten.
 */
export async function fetchTemplates(
    sub: string,
    hostname: string
): Promise<ServerTemplate[] | null> {
    if (!REST_BASE) {
        console.warn('[applicationTemplates] VITE_REST_URL/VITE_API_URL mangler');
        return null;
    }
    if (!sub) {
        console.warn('[applicationTemplates] OIDC sub mangler — kan ikke kalle REST-endepunktet');
        return null;
    }
    try {
        const url = `${REST_BASE}/applicationtemplate?sub=${encodeURIComponent(sub)}&hostname=${encodeURIComponent(hostname)}`;
        const res = await axios.get(url, { headers: { Accept: 'application/json' } });
        const rows: ServerTemplate[] = res.data?.data ?? [];
        console.log(`[applicationTemplates] /rest/applicationtemplate returnerte ${rows.length} maler`);
        return Array.isArray(rows) ? rows : [];
    } catch (error) {
        console.warn('[applicationTemplates] Kunne ikke hente maler:', error);
        return null;
    }
}

/**
 * Konverterer en serverside-template til samme flate form som legacy
 * `application_types`-oppføringer (slik at konsumenter ikke trenger å vite
 * hvor dataene kommer fra). `legacyMatch` brukes til å fylle inn felt som
 * fortsatt lever utenfor template-tabellen (periods, deadline, has_reports).
 */
export function toFlatApplicationConfig(
    tpl: ServerTemplate,
    legacyMatch?: Record<string, any>
): Record<string, any> {
    const legacy = legacyMatch ?? {};
    const inner = tpl.application ?? {};
    const hasLegacyMatch = !!legacyMatch;

    // `tpl.periods` er et JSON-objekt fra REST (dekodes i serialize() i controlleren).
    // Faller tilbake på legacy-felt hvis template ikke har migrert ennå.
    const tplPeriods = tpl.periods ?? null;

    // report_config er en liste over AKTIVERTE rapporttyper (opt-in).
    // has_reports avledes herfra — application.has_reports brukes ikke lenger.
    const enabledReports = Array.isArray(tpl.report_config) ? tpl.report_config : [];

    return {
        id: tpl.id,
        type: Number(tpl.type),
        title: tpl.title || legacy.title || '',
        url: tpl.alias || legacy.url || '',
        short_description: tpl.description ?? legacy.short_description,
        sections: inner.sections ?? legacy.sections ?? [],
        introtext: inner.introtext ?? legacy.introtext,
        has_reports: enabledReports.length > 0,
        custom_progress: inner.custom_progress ?? legacy.custom_progress ?? false,
        // Skal Iver veilede søkeren i dette skjemaet? Eneste kilde er malen
        // etter migrate_iver_prompts.php. MÅ stå her: funksjonen bygger
        // objektet fra en eksplisitt feltliste, så alt som ikke nevnes
        // forsvinner stille.
        assistantEnabled: inner.assistantEnabled === true,
        // Krever skjemaet organisasjonsnummer? REST sender den effektive
        // verdien (eksplisitt flagg, ellers utledet fra skjemaet —
        // portalApplicationTemplate::requiresOrgNr). `inner` dekker eldre
        // REST-svar uten feltet, `legacy` getJsonConfig-lista, og `true` er
        // backendens egen default. MÅ stå her: funksjonen bygger objektet med
        // en eksplisitt feltliste, så feltet forsvant stille før — og
        // «Opprett sak» i admin viste alltid organisasjonsnummer, også for
        // Ringerikskrafts sponsorat.
        requires_org_nr:
            tpl.requires_org_nr ??
            inner.requires_org_nr ??
            legacy.requires_org_nr ??
            true,
        // Periodsdata — primærkilde er nå tpl.periods JSON-kolonne
        periods: tplPeriods?.periods ?? legacy.periods,
        periods_text: tplPeriods
            ? {
                inside: tplPeriods.periods_text_inside ?? '',
                outside: tplPeriods.periods_text_outside ?? '',
                search_outside_periods: tplPeriods.search_outside_periods ?? false,
              }
            : (legacy.periods_text ?? (hasLegacyMatch ? undefined : { search_outside_periods: true })),
        deadline: legacy.deadline,
        startdate: legacy.startdate,
        // Synlighet og rekkefølge, fra portal_application_setting via REST.
        //
        // `published` er nå ENESTE kilde til om skjemaet tilbys søkere —
        // se portalApplicationTemplate::typeEntry og
        // migrate_public_to_published.php. Dashboard filtrerer på denne.
        //
        // Begge MÅ stå her: funksjonen bygger objektet med en eksplisitt
        // feltliste, så alt som ikke nevnes forsvinner stille.
        published: tpl.published ?? true,
        sort: Number(tpl.sort ?? 0),
        // Speil av `published`, beholdt fordi eldre kallesteder leser `public`.
        // Backend sender nå samme verdi i begge.
        public: tpl.published ?? legacy.public ?? (hasLegacyMatch ? undefined : true),
    };
}

/**
 * Henter OIDC `sub` synkront fra lageret oidc-client-ts bruker (se
 * helpers/oidcStorage.ts). Brukes når kall må skje utenfor
 * `<OidcAuthProvider>`-konteksten.
 */
export function readOidcSub(): string {
    return readStoredSub();
}
